import FigureThumb from '../FigureThumb'
import type { HomepageContent } from '../../cms/homepage'
import HomepageActionLink from './HomepageActionLink'
import HomepageSectionHeader from './HomepageSectionHeader'

type FeaturedExperiencePromptLink = {
  label: string
  href: string
}

type FeaturedExperienceSectionProps = {
  content: HomepageContent['featuredExperience']
  exploreHref: string
  conversationHref: string
  conversationLabel: string
  promptLinks: FeaturedExperiencePromptLink[]
  visualSrc: string
  visualAlt: string
  location?: string
  era?: string
  eraAccent?: string
  voiceHref: string
  voiceName: string
  voiceRole: string
  voiceSupport?: string
  voiceImageSrc?: string | null
}

export default function FeaturedExperienceSection({
  content,
  exploreHref,
  conversationHref,
  conversationLabel,
  promptLinks,
  visualSrc,
  visualAlt,
  location,
  era,
  eraAccent = 'medieval',
  voiceHref,
  voiceName,
  voiceRole,
  voiceSupport,
  voiceImageSrc,
}: FeaturedExperienceSectionProps) {
  return (
    <section className="home-section">
      <HomepageSectionHeader title={content.title} />
      <article className="home-featured-experience">
        <div className="home-featured-main">
          <div className="home-featured-meta">
            {era && <span className={`era-chip ${eraAccent}`}>{era}</span>}
            {location && <span className="home-featured-tag">{location}</span>}
          </div>
          <h3>{content.featureTitle}</h3>
          <p>{content.body}</p>
          <div className="button-row home-featured-actions">
            <HomepageActionLink className="button home-featured-explore-button" href={exploreHref}>
              {content.placeCtaLabel}
            </HomepageActionLink>
            <HomepageActionLink className="button primary home-featured-talk-button" href={conversationHref}>
              {conversationLabel}
            </HomepageActionLink>
          </div>
          <HomepageActionLink className={`home-featured-voice${voiceImageSrc ? '' : ' is-text-only'}`} href={voiceHref}>
            {voiceImageSrc && (
              <FigureThumb
                className="home-featured-voice-thumb"
                src={voiceImageSrc}
                alt={`${voiceName} portrait`}
                size={88}
                fallbackText={voiceName}
              />
            )}
            <div className="home-featured-voice-copy">
              <p className="home-featured-voice-label">Featured voice</p>
              <p className="home-featured-voice-name">{voiceName}</p>
              <p className="home-featured-voice-role">{voiceRole}</p>
              {voiceSupport && <p className="home-featured-voice-support">{voiceSupport}</p>}
            </div>
          </HomepageActionLink>
        </div>
        <aside className="home-featured-rail">
          <HomepageActionLink className="home-featured-media" href={exploreHref}>
            <img src={visualSrc} alt={visualAlt} loading="lazy" />
          </HomepageActionLink>
          <div className="home-featured-prompts-panel" aria-label="Suggested questions">
            <p className="home-featured-prompts-label">Try asking</p>
            <ul className="home-featured-prompts">
              {promptLinks.map((prompt) => (
                <li key={prompt.label}>
                  <HomepageActionLink className="home-featured-prompt-link" href={prompt.href}>
                    <span>{prompt.label}</span>
                  </HomepageActionLink>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </article>
    </section>
  )
}

